import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Routes } from '../../util/constants';
import {
  helpArticles,
  helpFaqs,
  helpTopics,
  type HelpTopicIcon,
} from './content';

const TOPIC_ICONS: Record<HelpTopicIcon, JSX.Element> = {
  'getting-started': <RocketLaunchOutlinedIcon />,
  customers: <PeopleOutlineIcon />,
  'sales-orders': <DescriptionOutlinedIcon />,
  'purchase-orders': <LocalShippingOutlinedIcon />,
  invoices: <ReceiptLongOutlinedIcon />,
  reports: <EqualizerOutlinedIcon />,
  settings: <SettingsOutlinedIcon />,
};

const SUPPORT_EMAIL = 'support@orderflow.app';

export default function Help() {
  const navigate = useNavigate();
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.HelpPage',
  });
  const [query, setQuery] = useState('');

  const normalizedQuery = query.trim().toLowerCase();

  const openArticle = (articleId: string | undefined) => {
    if (!articleId) return;
    navigate(`${Routes.HOME_HELP}/${articleId}`);
  };

  const filteredTopics = useMemo(() => {
    if (!normalizedQuery) return helpTopics;
    return helpTopics.filter(
      (topic) =>
        topic.title.toLowerCase().includes(normalizedQuery) ||
        topic.description.toLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery]);

  const filteredArticles = useMemo(() => {
    if (!normalizedQuery) return helpArticles;
    return helpArticles.filter(
      (article) =>
        article.title.toLowerCase().includes(normalizedQuery) ||
        article.summary.toLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery]);

  const filteredFaqs = useMemo(() => {
    if (!normalizedQuery) return helpFaqs;
    return helpFaqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(normalizedQuery) ||
        faq.answer.toLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery]);

  const hasResults =
    filteredTopics.length > 0 ||
    filteredArticles.length > 0 ||
    filteredFaqs.length > 0;

  return (
    <Box className="flex flex-col flex-1 mx-10 my-12 md:mx-20">
      <Typography variant="h5" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
        {t('Help Center')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('Subtitle')}
      </Typography>

      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('Search help articles')}
        sx={{ maxWidth: 560, mb: 4 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {!hasResults ? (
        <Typography variant="body1" color="text.secondary">
          {t('No results', { query })}
        </Typography>
      ) : (
        <>
          {filteredTopics.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('Browse topics')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                  },
                  gap: 3,
                }}
              >
                {filteredTopics.map((topic) => (
                  <ButtonBase
                    key={topic.id}
                    onClick={() => openArticle(topic.articleIds[0])}
                    sx={{
                      textAlign: 'left',
                      borderRadius: 2,
                      display: 'block',
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        transition: 'box-shadow 0.2s, border-color 0.2s',
                        '&:hover': {
                          boxShadow: 3,
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {TOPIC_ICONS[topic.icon]}
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {topic.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {topic.description}
                      </Typography>
                    </Paper>
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          )}

          {filteredArticles.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('Popular articles')}
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                {filteredArticles.map((article, index) => (
                  <ButtonBase
                    key={article.id}
                    onClick={() => openArticle(article.id)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'block',
                      p: 2,
                      borderTop: index === 0 ? 'none' : '1px solid',
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        >
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {article.summary}
                        </Typography>
                      </Box>
                      <ArrowForwardIcon color="action" fontSize="small" />
                    </Box>
                  </ButtonBase>
                ))}
              </Paper>
            </Box>
          )}

          {filteredFaqs.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('Frequently asked questions')}
              </Typography>
              {filteredFaqs.map((faq) => (
                <Accordion key={faq.id} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                    {faq.relatedArticleId && (
                      <Link
                        component="button"
                        underline="hover"
                        sx={{ mt: 1, display: 'inline-block', fontSize: 14 }}
                        onClick={() => openArticle(faq.relatedArticleId)}
                      >
                        {t('Read more')}
                      </Link>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </>
      )}

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          p: 3,
          borderRadius: 2,
          backgroundColor: 'action.hover',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('Still need help?')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('Contact support description')}
        </Typography>
        <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
          {t('Contact support')}
        </Link>
      </Paper>
    </Box>
  );
}
